import { ThemeEvents } from '@theme/events';

/**
 * Keeps the landing-page variant controls independent from the cart form.
 * Variant changes only update local form state; cart behavior starts with an
 * explicit add-to-cart submission.
 */
class SodiiCollaborationProduct extends HTMLElement {
  /** @type {Array<{id: number, available: boolean, options: string[], price: string, compareAtPrice: string | null, featuredMedia: string | null}>} */
  #variants = [];

  #awaitingCartAdd = false;
  #cartAddTimeout;
  #quickShopFrame;
  #detailsStrip;
  #faqSection;
  #faqBoundary;
  #faqResizeObserver;
  #purchaseOptions;
  #purchaseHome;
  #quickShop;
  #quickShopTrigger;
  #quickShopBuyNow;
  #quickShopClose;
  #quickShopScrollTarget;
  #quickShopExpanded;
  #expandedPurchaseSlot;
  #isQuickShopVisible = false;
  #isQuickShopExpanded = false;
  #suppressQuickShopExpansion = false;

  connectedCallback() {
    this.#variants = this.#readVariants();

    this.addEventListener('change', this.#handleVariantChange);
    this.addEventListener('submit', this.#handleSubmit, true);
    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener(ThemeEvents.cartError, this.#handleCartError);

    this.#syncSelectedVariant();
    this.#setupQuickShop();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleVariantChange);
    this.removeEventListener('submit', this.#handleSubmit, true);
    this.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener(ThemeEvents.cartError, this.#handleCartError);

    window.removeEventListener('scroll', this.#queueQuickShopUpdate);
    window.removeEventListener('resize', this.#queueQuickShopUpdate);
    document.removeEventListener('shopify:section:load', this.#queueQuickShopUpdate);
    this.#quickShopTrigger?.removeEventListener('click', this.#handleQuickShopClick);
    this.#quickShopBuyNow?.removeEventListener('click', this.#handleQuickShopBuyNow);
    this.#quickShopClose?.removeEventListener('click', this.#handleQuickShopClose);
    this.removeEventListener('keydown', this.#handleQuickShopKeydown);
    this.#faqResizeObserver?.disconnect();

    if (this.#quickShopFrame) cancelAnimationFrame(this.#quickShopFrame);

    if (this.#cartAddTimeout) clearTimeout(this.#cartAddTimeout);

    this.#restorePurchaseOptions();
  }

  #setupQuickShop() {
    this.#quickShop = this.querySelector('[data-sodii-quick-shop]');
    this.#quickShopTrigger = this.querySelector('[data-sodii-quick-shop-trigger]');
    this.#quickShopBuyNow = this.querySelector('[data-sodii-quick-shop-buy-now]');
    this.#quickShopClose = this.querySelector('[data-sodii-quick-shop-close]');
    this.#quickShopScrollTarget = this.querySelector('[data-sodii-quick-shop-scroll-target]');
    this.#quickShopExpanded = this.querySelector('[data-sodii-quick-shop-expanded]');
    this.#purchaseOptions = this.querySelector('[data-sodii-purchase-options]');
    this.#purchaseHome = this.querySelector('[data-sodii-purchase-home]');
    this.#expandedPurchaseSlot = this.querySelector('[data-sodii-expanded-purchase-slot]');

    if (!this.#quickShop || !this.#quickShopTrigger || !this.#purchaseOptions) return;

    this.#quickShopTrigger.addEventListener('click', this.#handleQuickShopClick);
    this.#quickShopBuyNow?.addEventListener('click', this.#handleQuickShopBuyNow);
    this.#quickShopClose?.addEventListener('click', this.#handleQuickShopClose);
    this.addEventListener('keydown', this.#handleQuickShopKeydown);
    window.addEventListener('scroll', this.#queueQuickShopUpdate, { passive: true });
    window.addEventListener('resize', this.#queueQuickShopUpdate, { passive: true });
    document.addEventListener('shopify:section:load', this.#queueQuickShopUpdate);
    this.#queueQuickShopUpdate();
  }

  #queueQuickShopUpdate = () => {
    if (this.#quickShopFrame) return;

    this.#quickShopFrame = requestAnimationFrame(() => {
      this.#quickShopFrame = undefined;
      this.#updateQuickShopVisibility();
    });
  };

  #updateQuickShopVisibility() {
    if (!this.#quickShop || !this.#quickShopTrigger) return;

    if (!this.#detailsStrip?.isConnected) this.#detailsStrip = this.#findQuickShopRevealTarget();

    const shouldShow = Boolean(this.#detailsStrip && this.#detailsStrip.getBoundingClientRect().bottom <= 0);
    const panelMode = this.dataset.quickShopPanelMode;
    const isManualPanel = panelMode === 'manual';
    const isScrollPanel = panelMode === 'scroll';
    let shouldExpand = false;

    if (shouldShow && isManualPanel) {
      shouldExpand = this.#isQuickShopExpanded;
    } else if (shouldShow && !isScrollPanel) {
      const shouldAutoExpand = this.#shouldExpandQuickShop();

      if (panelMode === 'actions' && this.#suppressQuickShopExpansion) {
        this.#suppressQuickShopExpansion = shouldAutoExpand;
      } else {
        shouldExpand = shouldAutoExpand;
      }
    }

    this.#setQuickShopExpanded(shouldExpand);
    const isDockedAboveViewport = isManualPanel || isScrollPanel ? false : this.#updateQuickShopDocking(shouldExpand);
    const isScrollPanelVisible = isScrollPanel && this.#isQuickShopScrollTargetVisible();
    this.#setQuickShopVisibility(shouldShow && !isDockedAboveViewport && !isScrollPanelVisible);
  }

  #findQuickShopRevealTarget() {
    const sectionId = this.dataset.quickShopRevealSectionId?.trim();
    if (!sectionId) return document.querySelector('.sodii-product-details-strip');

    return (
      document.getElementById(sectionId) ||
      Array.from(document.querySelectorAll('[id]')).find((element) => element.id.endsWith(`__${sectionId}`)) ||
      null
    );
  }

  #isQuickShopScrollTargetVisible() {
    if (!this.#quickShopScrollTarget?.isConnected) {
      this.#quickShopScrollTarget = this.querySelector('[data-sodii-quick-shop-scroll-target]');
    }

    if (!this.#quickShopScrollTarget) return false;

    const targetRect = this.#quickShopScrollTarget.getBoundingClientRect();
    return targetRect.top < window.innerHeight && targetRect.bottom > 0;
  }

  #shouldExpandQuickShop() {
    if (!this.#faqSection?.isConnected || !this.#faqBoundary?.isConnected) {
      this.#faqResizeObserver?.disconnect();
      this.#faqSection = document.querySelector('[data-sodii-faq-section]');
      this.#faqBoundary = this.#faqSection?.querySelector('[data-sodii-faq-boundary]');

      if (this.#faqSection && 'ResizeObserver' in window) {
        this.#faqResizeObserver = new ResizeObserver(this.#queueQuickShopUpdate);
        this.#faqResizeObserver.observe(this.#faqSection);
      }
    }

    if (!this.#faqSection || !this.#faqBoundary) return false;

    const boundaryRect = this.#faqBoundary.getBoundingClientRect();
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const expandedHeight = isMobile
      ? Math.min(window.innerHeight * 0.78, 720)
      : Math.min(520, window.innerHeight - 48);
    const activationLine = Math.max(24, window.innerHeight - expandedHeight - 24);

    // Keep the closing card expanded after the FAQ trigger. It contracts only
    // when scrolling back above that boundary.
    return boundaryRect.top <= activationLine;
  }

  #updateQuickShopDocking(isExpanded) {
    if (!this.#quickShop) return false;

    let dockOffset = 0;
    let isDockedAboveViewport = false;

    if (isExpanded && this.#faqSection) {
      const sectionBottom = this.#faqSection.getBoundingClientRect().bottom;
      dockOffset = Math.max(0, window.innerHeight - sectionBottom);
      isDockedAboveViewport = sectionBottom <= 0;
    }

    this.#quickShop.style.setProperty('--sodii-quick-shop-dock-offset', `${Math.round(dockOffset)}px`);
    this.#quickShop.classList.toggle('is-docked', dockOffset > 0);

    return isDockedAboveViewport;
  }

  #setQuickShopVisibility(isVisible) {
    if (!this.#quickShop || !this.#quickShopTrigger) return;

    this.#isQuickShopVisible = isVisible;
    this.#quickShop.classList.toggle('is-visible', isVisible);
    this.#quickShop.setAttribute('aria-hidden', (!isVisible).toString());

    if (isVisible && !this.#isQuickShopExpanded) {
      this.#quickShopTrigger.removeAttribute('tabindex');
    } else {
      this.#quickShopTrigger.tabIndex = -1;
    }
  }

  #setQuickShopExpanded(isExpanded) {
    const isActionPanel = this.dataset.quickShopPanelMode === 'actions';

    if (
      !this.#quickShop ||
      !this.#quickShopTrigger ||
      !this.#quickShopExpanded ||
      !this.#purchaseOptions ||
      (!isActionPanel && (!this.#purchaseHome || !this.#expandedPurchaseSlot)) ||
      this.#isQuickShopExpanded === isExpanded
    ) {
      return;
    }

    this.#isQuickShopExpanded = isExpanded;
    this.#quickShop.classList.toggle('is-expanded', isExpanded);
    this.#quickShopTrigger.setAttribute('aria-expanded', isExpanded.toString());
    this.#quickShopExpanded.setAttribute('aria-hidden', (!isExpanded).toString());

    if (isExpanded) {
      if (!isActionPanel) this.#expandedPurchaseSlot.append(this.#purchaseOptions);
      this.#quickShopExpanded.removeAttribute('inert');
      this.#quickShopTrigger.tabIndex = -1;
    } else {
      this.#quickShopExpanded.setAttribute('inert', '');
      if (!isActionPanel) this.#restorePurchaseOptions();

      if (this.#isQuickShopVisible) {
        this.#quickShopTrigger.removeAttribute('tabindex');
      }
    }
  }

  #restorePurchaseOptions() {
    if (!this.#purchaseHome || !this.#purchaseOptions) return;

    this.#purchaseHome.append(this.#purchaseOptions);
  }

  #handleQuickShopClick = () => {
    if (!this.#purchaseOptions) return;

    if (this.dataset.quickShopPanelMode === 'scroll' && this.#quickShopScrollTarget) {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.#quickShopScrollTarget.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      this.#quickShopScrollTarget.focus({ preventScroll: true });
      return;
    }

    if (this.dataset.quickShopPanelMode === 'manual') {
      this.#setQuickShopExpanded(true);
      this.#setQuickShopVisibility(true);
      requestAnimationFrame(() => this.#quickShopClose?.focus());
      return;
    }

    this.#scrollToPurchaseOptions();
  };

  #handleQuickShopBuyNow = () => {
    this.#suppressQuickShopExpansion = true;
    this.#setQuickShopExpanded(false);
    this.#scrollToPurchaseOptions();
  };

  #scrollToPurchaseOptions() {
    if (!this.#purchaseOptions) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const focusTarget = this.#purchaseOptions.querySelector('[data-sodii-option]:checked, [ref="addToCartButton"]');

    this.#setQuickShopVisibility(false);
    this.#purchaseOptions.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'center',
    });

    if (focusTarget instanceof HTMLElement) {
      focusTarget.focus({ preventScroll: true });
    }
  }

  #handleQuickShopClose = () => {
    if (this.dataset.quickShopPanelMode !== 'manual') return;

    this.#setQuickShopExpanded(false);
    this.#setQuickShopVisibility(true);
    this.#quickShopTrigger?.focus();
  };

  #handleQuickShopKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#isQuickShopExpanded || this.dataset.quickShopPanelMode !== 'manual') return;

    event.preventDefault();
    this.#handleQuickShopClose();
  };

  #readVariants() {
    const dataElement = this.querySelector('[data-sodii-variants]');
    if (!dataElement?.textContent) return [];

    try {
      return JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Unable to read Sodii product variants.', error);
      return [];
    }
  }

  #handleVariantChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-sodii-option]')) return;

    this.#syncSelectedVariant();
  };

  #handleSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement) || event.target.dataset.type !== 'add-to-cart-form') return;

    const submitter = event.submitter;
    if (!(submitter instanceof HTMLButtonElement) || submitter.getAttribute('ref') !== 'addToCartButton') {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }

    this.#awaitingCartAdd = true;
    if (this.#cartAddTimeout) clearTimeout(this.#cartAddTimeout);
    this.#cartAddTimeout = setTimeout(() => {
      this.#awaitingCartAdd = false;
    }, 15000);
  };

  #handleCartUpdate = (event) => {
    if (!this.#awaitingCartAdd || event.detail?.data?.source !== 'product-form-component') return;

    this.#clearPendingCartAdd();
    if (event.detail?.data?.didError) return;

    const cartDrawer = document.querySelector('cart-drawer-component');
    cartDrawer?.open?.();
  };

  #handleCartError = () => {
    this.#clearPendingCartAdd();
  };

  #clearPendingCartAdd() {
    this.#awaitingCartAdd = false;
    if (this.#cartAddTimeout) clearTimeout(this.#cartAddTimeout);
  }

  #syncSelectedVariant() {
    if (!this.#variants.length) return;

    const optionGroups = Array.from(this.querySelectorAll('[data-sodii-option-group]'));
    const selectedOptions = optionGroups.map((group) => {
      const checkedOption = group.querySelector('[data-sodii-option]:checked');
      return checkedOption instanceof HTMLInputElement ? checkedOption.value : '';
    });

    const selectedVariant = this.#variants.find(
      (variant) =>
        variant.options.length === selectedOptions.length &&
        variant.options.every((option, index) => option === selectedOptions[index])
    );

    this.#updateOptionAvailability(selectedOptions);
    this.#updateProductForm(selectedVariant);
    this.#updatePrice(selectedVariant);
  }

  #updateOptionAvailability(selectedOptions) {
    const options = this.querySelectorAll('[data-sodii-option]');

    for (const option of options) {
      if (!(option instanceof HTMLInputElement)) continue;

      const optionIndex = Number(option.dataset.optionIndex);
      const isAvailable = this.#variants.some(
        (variant) =>
          variant.available &&
          variant.options[optionIndex] === option.value &&
          variant.options.every(
            (variantOption, index) => index === optionIndex || variantOption === selectedOptions[index]
          )
      );

      option.disabled = !isAvailable && !option.checked;
      option.dataset.optionAvailable = isAvailable.toString();
      option.closest('label')?.classList.toggle('is-unavailable', !isAvailable);
    }
  }

  #updateProductForm(variant) {
    const variantInput = this.querySelector('[ref="variantId"]');
    const addToCartButton = this.querySelector('[ref="addToCartButton"]');
    const addToCartContainer = this.querySelector('[ref="addToCartButtonContainer"]');

    if (variantInput instanceof HTMLInputElement) {
      variantInput.value = variant?.id?.toString() || '';
    }

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !variant?.available;

      const buttonText = addToCartButton.querySelector('.add-to-cart-text__content');
      if (buttonText) {
        buttonText.textContent = variant
          ? variant.available
            ? this.dataset.addToCartLabel || 'Add to cart'
            : this.dataset.soldOutLabel || 'Sold out'
          : this.dataset.unavailableLabel || 'Unavailable';
      }
    }

    if (addToCartContainer instanceof HTMLElement) {
      if (variant?.featuredMedia) {
        addToCartContainer.dataset.productVariantMedia = variant.featuredMedia;
      } else {
        addToCartContainer.removeAttribute('data-product-variant-media');
      }
    }
  }

  #updatePrice(variant) {
    const price = this.querySelector('[data-sodii-current-price]');
    const compareAtPrice = this.querySelector('[data-sodii-compare-price]');

    if (price) price.textContent = variant?.price || '';

    if (compareAtPrice instanceof HTMLElement) {
      compareAtPrice.textContent = variant?.compareAtPrice || '';
      compareAtPrice.hidden = !variant?.compareAtPrice;
    }
  }
}

if (!customElements.get('sodii-collaboration-product')) {
  customElements.define('sodii-collaboration-product', SodiiCollaborationProduct);
}
